import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classnames from 'classnames';
import { compact, flatten } from 'lodash-es';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { withCoreMediaWrapper } from 'site-modules/shared/components/core-page/core-media-wrapper/core-media-wrapper';
import { useScrollArrows } from 'site-modules/shared/hooks/use-scroll-arrows';
import { OVERLAY_TYPE, PHOTO_ROW_THRESHOLD } from 'site-modules/core-page/utils/photo-gallery';

import { PhotoGalleryPhoto } from './photo-gallery-photo';

const ARROW_CLASS_NAME =
  'arrow pos-a center-y d-flex align-items-center justify-content-center bg-white border-0 text-blue-40';

export function PhotoGalleryRowUI({
  coreMedia = null,
  interiorPhotos = null,
  params,
  isPreprod = false,
  trackPhotosProvider,
  isCore6981Test = false,
  isMobile = false,
}) {
  const photos = flatten(compact([interiorPhotos, coreMedia?.allMedia?.photo]));

  const startIndex = isPreprod ? 1 : 0;
  const renderPhotos =
    photos?.length >= PHOTO_ROW_THRESHOLD && photos.slice(startIndex, startIndex + PHOTO_ROW_THRESHOLD);

  useEffect(() => {
    trackPhotosProvider(renderPhotos);
  }, [trackPhotosProvider, renderPhotos]);

  const carouselRef = useRef();
  const [arrows, scrollLeft, scrollRight] = useScrollArrows(carouselRef, { isEnabled: isMobile });

  const photoItems = renderPhotos ? (
    renderPhotos.map((photo, index) => {
      let overlayType;

      if (!isMobile) {
        switch (index) {
          case PHOTO_ROW_THRESHOLD - 1: {
            overlayType = OVERLAY_TYPE.REST_PHOTOS;
            break;
          }
          default: {
            break;
          }
        }
      }

      return (
        <Col xs={10} md={3} key={photo.src}>
          <PhotoGalleryPhoto
            media={coreMedia.allMedia}
            photoData={photo}
            params={params}
            isPreprod={isPreprod}
            totalCount={coreMedia.totalCount}
            overlayType={overlayType}
            index={index + 1}
            isCore6981Test={isCore6981Test}
            isMobile={isMobile}
          />
        </Col>
      );
    })
  ) : (
    // Empty one for critical css
    <Col xs={10} md={3} />
  );

  if (isMobile) {
    return (
      <div className="photo-wrapper pos-r mt-0_5">
        <button
          className={classnames(ARROW_CLASS_NAME, 'left left-0', { invisible: !arrows.left })}
          onClick={scrollLeft}
          aria-label="Scroll photos left"
        >
          <i className="icon-arrow-left3" aria-hidden />
        </button>
        <div ref={carouselRef} className="row photos-carousel flex-nowrap overflow-x-auto">
          {photoItems}
        </div>
        <button
          className={classnames(ARROW_CLASS_NAME, 'right right-0', { invisible: !arrows.right })}
          onClick={scrollRight}
          aria-label="Scroll photos right"
        >
          <i className="icon-arrow-right3" aria-hidden />
        </button>
      </div>
    );
  }

  return <Row className="photo-wrapper pos-r d-none d-lg-flex mt-0_5">{photoItems}</Row>;
}

PhotoGalleryRowUI.propTypes = {
  params: CorePageParams.isRequired,
  trackPhotosProvider: PropTypes.func.isRequired,
  coreMedia: PropTypes.shape({
    totalCount: PropTypes.number,
    allMedia: PropTypes.shape({
      photos: PropTypes.arrayOf(PropTypes.shape({})),
      photo: PropTypes.arrayOf(PropTypes.shape({})),
      videos: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  interiorPhotos: PropTypes.arrayOf(PropTypes.shape({})),
  isPreprod: PropTypes.bool,
  isCore6981Test: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const PhotoGalleryRow = withCoreMediaWrapper(PhotoGalleryRowUI);
